import React from "react";
import PropTypes from "prop-types";
/**
 * @fileoverview Rx History Modal Component
 * @module components/modals/RxHistoryModal
 * @description A modal component for displaying medication history with a tabular layout.
 * Supports different data formats for opportunities and medication history views.
 *
 * @requires react
 * @requires @mui/material
 * @requires @mui/material/styles
 * @requires @mui/icons-material/Close
 */

import {
  Dialog,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { formatPhoneNumber } from "../../utils/formatUtils";
import Tooltip from "@mui/material/Tooltip";
import { capitalizeFirstLetter } from "../../utils/capitalize";
import FocusableScrollContainer from "../common/FocusableScrollContainer";
import phoneIcon from "../../assets/Phone.svg";

/**
 * Styled MUI Dialog component with custom styling
 * @constant {Object} StyledDialog
 */
const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    borderRadius: "8px",
    width: "1000px",
    maxWidth: "95vw",
    backgroundColor: "#F7F8FB",
  },
});

/**
 * Styled component for the modal header
 * @constant {Object} ModalHeader
 */
const ModalHeader = styled("div")({
  backgroundColor: "#F7F8FB",
  padding: "4px 20px 10px",
  display: "flex",
  fontWeight: 700,
  fontSize: "16px",
  lineHeight: "24px",
  justifyContent: "space-between",
  alignItems: "center",
});

/**
 * Styled component for the modal content area
 * @constant {Object} ModalContent
 */
const ModalContent = styled("div")({
  backgroundColor: "#F7F8FB",
  padding: "0px 16px",
});

/**
 * Styled table container with transparent background
 * @constant {Object} StyledTableContainer
 */
const StyledTableContainer = styled(TableContainer)(({ sx = {} }) => ({
  backgroundColor: "transparent",
  boxShadow: "none",
  border: "none",
  maxHeight: 300,
  overflowY: "auto",
  ...sx,
}));

/**
 * Styled table header with custom background
 * @constant {Object} StyledTableHead
 */
const StyledTableHead = styled(TableHead)({
  backgroundColor: "#CFCEDA",
  position: "sticky",
  top: 0,
  zIndex: 2,
});

/**
 * Styled table header cell with custom typography and borders
 * @constant {Object} StyledTableCell
 */
const StyledTableCell = styled(TableCell)({
  fontWeight: 600,
  fontSize: "14px",

  borderBottom: "none",
  borderRight: "2px solid #ECEDF2",
  color: "#403C6F",
  "&:last-child": {
    borderRight: "none",
  },
});

/**
 * Styled table body cell with custom styling
 * @constant {Object} StyledTableBodyCell
 */
const StyledTableBodyCell = styled(TableCell)({
  fontSize: "14px",
  padding: "16px",
  borderBottom: "none",
  backgroundColor: "#FFFFFF",

  "&:last-child": {
    borderRight: "none",
  },
});

/**
 * Styled table row with custom height and background
 * @constant {Object} StyledTableRow
 */
const StyledTableRow = styled(TableRow)({
  backgroundColor: "#FFFFFF",
  height: "60px",
  "& td": {
    backgroundColor: "#FFFFFF",
  },
});

/**
 * Formats a date string to MM/DD/YYYY format
 * @param {string} dateStr - The date string to format
 * @returns {string} Formatted date string
 */
const formatDate = (dateStr) => {
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    return dateStr;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-");
    return `${month}/${day}/${year}`;
  }

  if (/^\d{2}-[A-Z]{3}-\d{2}$/.test(dateStr)) {
    const months = {
      JAN: "01",
      FEB: "02",
      MAR: "03",
      APR: "04",
      MAY: "05",
      JUN: "06",
      JUL: "07",
      AUG: "08",
      SEP: "09",
      OCT: "10",
      NOV: "11",
      DEC: "12",
    };
    const [day, mon, year] = dateStr.split("-");
    const month = months[mon.toUpperCase()] || "01";

    const fullYear = Number.parseInt(year, 10) > 50 ? `19${year}` : `20${year}`;
    return `${month}/${day}/${fullYear}`;
  }

  return dateStr;
};

/**
 * RxHistoryModal Component
 *
 * A modal dialog that displays medication history in a tabular format.
 * Supports different data formats for opportunities and medication history views.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls modal visibility
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Array} [props.historyData=[]] - Array of medication history data
 * @param {string} [props.type="opportunities"] - Type of history to display ("opportunities" or "medicationHistory")
 * @param {boolean} [props.loading=false] - Whether the component is in a loading state
 * @param {Object} [props.tableContainerProps={}] - Additional props to pass to the table container
 * @returns {JSX.Element} The rendered modal component
 */
const RxHistoryModal = ({
  open,
  onClose,
  historyData = [],
  type = "opportunities",
  tableContainerProps = {},
  loading = false,
  isShowPhiEnabled = false,
}) => {
  /**
   * Handles modal close action
   */
  const handleClose = () => {
    onClose();
  };

  /**
   * Format history data based on the component type
   * @type {Array}
   */
  let formattedHistory = [];

  formattedHistory = Array.isArray(historyData)
    ? historyData.map((item, idx) => {
        return {
          id: item.id || idx,
          claimNumber: item.authNum || "N/A",
          memberCost:
            item.memberCost !== undefined && !Number.isNaN(item.memberCost)
              ? `$${Number(item.memberCost).toFixed(2)}`
              : "$0.00",
          lastFilled: formatDate(item.lastFilled) || "",
          pharmacyName: item.pharmacyName || "N/A",
          pharmacyPhone: item.pharmacyPhone
            ? formatPhoneNumber(item.pharmacyPhone, isShowPhiEnabled)
            : "N/A",
          providerName: item.providerName || "N/A",
          providerPhone: item.providerPhone
            ? formatPhoneNumber(item.providerPhone, isShowPhiEnabled)
            : "N/A",
        };
      })
    : [];

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <ModalHeader>
        <Typography
          variant="h6"
          sx={{
            color: "#10100F",
            fontWeight: 600,
            fontSize: "18px",
            lineHeight: "32px",
          }}
        >
          Rx History
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </ModalHeader>

      <ModalContent>
        <FocusableScrollContainer
          component="section"
          label="Medication history table"
        >
          <StyledTableContainer component={Paper} {...tableContainerProps}>
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="100px"
              >
                <CircularProgress />
              </Box>
            ) : (
              <Table>
                <StyledTableHead>
                  <TableRow>
                    <StyledTableCell sx={{ width: "20%" }}>
                      Claim Number
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ width: "16%", whiteSpace: "nowrap" }}
                    >
                      Member Cost
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: "13%" }}>
                      Last Filled
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: "24%" }}>
                      Provider
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: "30%" }}>
                      Pharmacy
                    </StyledTableCell>
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      sx={{
                        padding: "2px",
                        border: "none",
                        backgroundColor: "transparent",
                      }}
                    />
                  </TableRow>
                  {formattedHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        No medication history found
                      </TableCell>
                    </TableRow>
                  ) : (
                    formattedHistory.map((row, index) => (
                      <React.Fragment key={row.id || index}>
                        <StyledTableRow>
                          <StyledTableBodyCell>
                            {row.claimNumber}
                          </StyledTableBodyCell>
                          <StyledTableBodyCell
                            sx={{ width: "16%", whiteSpace: "nowrap" }}
                          >
                            {typeof row.memberCost === "string"
                              ? row.memberCost
                              : `$${Number(row.memberCost).toFixed(2)}`}
                          </StyledTableBodyCell>
                          <StyledTableBodyCell sx={{ width: "13%" }}>
                            {formatDate(row.lastFilled)}
                          </StyledTableBodyCell>
                          <StyledTableBodyCell sx={{ width: "20%" }}>
                            <Tooltip
                              title={capitalizeFirstLetter(row.providerName)}
                              placement="top"
                              arrow
                            >
                              <div
                                style={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: 180,
                                }}
                              >
                                {capitalizeFirstLetter(row.providerName)}
                              </div>
                            </Tooltip>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <Tooltip title="Phone" arrow>
                                  <img
                                    src={phoneIcon}
                                    alt="Phone"
                                    style={{ width: "16px", height: "16px" }}
                                  />
                                </Tooltip>
                                {row.providerPhone}
                              </div>
                          </StyledTableBodyCell>
                          <StyledTableBodyCell sx={{ width: "34%" }}>
                            <Tooltip
                              title={capitalizeFirstLetter(row.pharmacyName)}
                              placement="top"
                              arrow
                            >
                              <div
                                style={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: 180,
                                }}
                              >
                                {capitalizeFirstLetter(row.pharmacyName)}
                              </div>
                            </Tooltip>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <Tooltip title="Phone" arrow>
                                  <img
                                    src={phoneIcon}
                                    alt="Phone"
                                    style={{ width: "16px", height: "16px" }}
                                  />
                                </Tooltip>
                                {row.pharmacyPhone}
                              </div>
                          </StyledTableBodyCell>
                        </StyledTableRow>
                        {index < formattedHistory.length - 1 && (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              sx={{
                                padding: "2px",
                                border: "none",
                                backgroundColor: "transparent",
                              }}
                            />
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </StyledTableContainer>
        </FocusableScrollContainer>
      </ModalContent>
    </StyledDialog>
  );
};

RxHistoryModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  historyData: PropTypes.array,
  type: PropTypes.string,
  tableContainerProps: PropTypes.object,
  loading: PropTypes.bool,
  isShowPhiEnabled: PropTypes.bool,
};

export default RxHistoryModal;
